import { useId, useMemo, useRef, useState } from 'react';

import { cn } from 'lib/helpers/utility';
import { useUIContext } from 'lib/context/UIContext/UIContext';

import Icon from 'components/shared/Icon/Icon';
import Typography from 'components/shared/Typography/Typography';
import RadioButton from 'components/shared/RadioButton/RadioButton';

import { CaptionDesktop, CaptionMobile } from './Caption';

const Disclosure = ({
  showMore = false,
  title,
  caption,
  icon,
  children,
  defaultOpen = false,
  isOpen: controlledOpen,
  onToggle,
  className,
  disableOpen = false,
  info,
  buttonClassName,
  bodyClassName,
  disabled = false,
  iconColor,
  iconShade,
  iconSize = 18,
  customTitle,
  radio = false,
  titleSize = 'small',
  captionSize = 'xs',
  alwaysShowChildren = false,
  iconBackground,
  trailing = null,
  contentClassName
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const [showInfo, setShowInfo] = useState(false);

  const { device } = useUIContext();
  const isMobile = device === 'mobile' || device === 'tablet-sm';

  const isControlled = typeof controlledOpen === 'boolean';
  const isOpen = useMemo(
    () => (isControlled ? controlledOpen : internalOpen),
    [controlledOpen, isControlled, internalOpen]
  );

  const contentRef = useRef(null);
  const contentId = useId();

  const handleToggle = () => {
    onToggle?.(!isOpen);
    if (!isControlled) setInternalOpen((prev) => !prev);
  };

  const handleShowInfo = () => {
    setShowInfo((prev) => !prev);
  };

  const shouldSetHeight = isOpen && !disableOpen;
  const showStaticChildren = alwaysShowChildren && disableOpen && !!children;
  const trailingNode =
    typeof trailing === 'function' ? trailing({ isOpen: shouldSetHeight, disabled }) : trailing;

  return (
    <div
      className={cn(
        'relative h-fit w-full rounded-lg border border-solid transition-all duration-200',
        '[@media(hover:hover)]:hover:border-primary-300 [@media(hover:hover)]:hover:shadow-card-selected [@media(hover:hover)]:hover:ring-2 [@media(hover:hover)]:hover:ring-primary-200',
        isOpen
          ? 'border-primary-300 bg-white shadow-card-selected ring-2 ring-primary-200'
          : 'overflow-hidden border-neutral-200 bg-white',
        className
      )}>
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          'flex w-full cursor-pointer gap-2 rounded-lg bg-white px-3 py-3.5 text-left',
          buttonClassName,
          disabled && 'opacity-50'
        )}
        aria-expanded={isOpen && !disableOpen}
        aria-controls={contentId}
        disabled={disabled}>
        {radio && (
          <RadioButton
            isSelected={isOpen}
            onChange={() => {}}
            disabled={disabled}
            tabIndex={-1}
            aria-hidden="true"
            wrapperClassName={cn(
              'pointer-events-none flex w-fit items-center self-center sm:hidden',
              titleSize === 'medium' ? 'h-6' : 'h-5'
            )}
          />
        )}
        {icon && typeof icon === 'string' ? (
          iconBackground ? (
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center gap-2 rounded-full p-2',
                iconBackground
              )}>
              <Icon
                icon={icon}
                size={iconSize}
                color={iconColor ?? (isOpen ? 'primary' : 'neutral')}
                shade={iconShade ?? (isOpen ? 600 : 700)}
              />
            </div>
          ) : (
            <Icon
              icon={icon}
              size={iconSize}
              color={iconColor ?? (isOpen ? 'primary' : 'neutral')}
              shade={iconShade ?? (isOpen ? 600 : 700)}
              className="mt-0.5 self-start"
            />
          )
        ) : (
          icon || null
        )}
        <div className="flex w-full flex-col gap-1.5">
          <div className={cn('flex w-full justify-between gap-2', bodyClassName)}>
            {customTitle ? (
              customTitle
            ) : (
              <Typography
                variant="p"
                size={titleSize}
                color={iconBackground ? 'neutral-900' : 'neutral-800'}
                weight={iconBackground ? 'medium' : 'semibold'}
                className="cursor-pointer leading-5">
                {title}
              </Typography>
            )}

            {(caption || info) && (
              <>
                {isMobile && showMore ? (
                  <Typography
                    variant="p"
                    size={captionSize}
                    color="primary-500"
                    weight="medium"
                    className="cursor-pointer leading-4"
                    onClick={handleShowInfo}>
                    {showInfo ? 'Show less' : 'Show more'}
                  </Typography>
                ) : (
                  <CaptionDesktop caption={caption} info={info} size={captionSize} />
                )}
              </>
            )}
          </div>
          {isMobile && info && showInfo && <CaptionMobile info={info} size={captionSize} />}
        </div>
        {trailingNode && <div className="flex shrink-0 items-center gap-3">{trailingNode}</div>}
      </button>
      <div
        ref={contentRef}
        style={{
          maxHeight: shouldSetHeight ? contentRef.current?.scrollHeight : 0,
          minHeight: shouldSetHeight ? 'fit-content' : 0
        }}
        id={contentId}
        role="region"
        aria-label={typeof title === 'string' ? title : 'Section'}
        className={cn('overflow-hidden px-3 transition-all duration-300', contentClassName)}>
        <div className="py-2">{showStaticChildren ? null : children}</div>
      </div>
      {showStaticChildren && <div className="px-3 pb-3">{children}</div>}
    </div>
  );
};

export default Disclosure;
